import { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Box, Typography, Skeleton } from "@mui/material";
import { useUserDistribution } from "hooks/use-user-distribution";

const COLORS = {
  personal: "#534AB7",
  corporate: "#1D9E75",
  admin: "#EF9F27",
  unknown: "#9E9E9E",
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" fontWeight={500} display="block">
        {item.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {item.count_formatted} users
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {item.percent}% of total
      </Typography>
    </Box>
  );
};

const CenterLabel = ({ cx, cy, total_formatted }) => {
  return (
    <>
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: 22,
          fontWeight: 500,
          fill: "var(--color-text-primary)",
        }}
      >
        {total_formatted}
      </text>
      <text
        x={cx}
        y={cy + 16}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 11, fill: "#888780" }}
      >
        Total users
      </text>
    </>
  );
};

const UserDistributionChart = () => {
  const { distribution: data, isLoading } = useUserDistribution();
  const [activeIndex, setActiveIndex] = useState(null);

  if (isLoading)
    return (
      <Skeleton
        variant="circular"
        width={220}
        height={220}
        sx={{ mx: "auto" }}
      />
    );
  if (!data) return null;

  return (
    <Box>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={data.distribution}
            cx="50%"
            cy="50%"
            innerRadius={75}
            outerRadius={110}
            paddingAngle={3}
            dataKey="count"
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            label={false}
          >
            {data.distribution.map((entry) => (
              <Cell
                key={entry.id}
                fill={COLORS[entry.id]}
                opacity={
                  activeIndex === null ||
                  activeIndex === data.distribution.indexOf(entry)
                    ? 1
                    : 0.4
                }
                stroke="none"
              />
            ))}

            <CenterLabel
              cx="50%"
              cy="50%"
              total_formatted={data.total_formatted}
            />
          </Pie>

          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 1 }}>
        {data.distribution.map((item) => (
          <Box
            key={item.id}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: COLORS[item.id],
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
            <Typography variant="caption" fontWeight={500}>
              {item.percent}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default UserDistributionChart;
